import React, { useState } from 'react';
import {
  IReasonLandRegisterQuery,
  IRelationshipLandRegisterQuery,
} from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  deDE,
  GridColDef,
  GridRenderCellParams,
  GridValueGetterParams,
} from '@mui/x-data-grid';
import { Delete, Edit } from '@mui/icons-material';
import {
  ILegalTransactionRelationshipRealEstate,
  initRealEstate,
} from '../../../../../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import { EditLandRegisterQueryOverview } from '../Components/EditLandRegisterQueryOverview';
import { IConfig } from '../../../../../../Interfaces/IConfig';

interface IProps {
  config: IConfig;
  relationship: IRelationshipLandRegisterQuery;
  setRelationsship: Function;
  handleBack: Function;
  handleNext: Function;
}

export const LandRegisterQueryOverview: React.FC<IProps> = ({
  config,
  relationship,
  setRelationsship,
  handleBack,
  handleNext,
}) => {
  const [idRealEstate, setIdRealEstate] = useState<null | number>(null);
  const [editRealEstate, setEditRealEstate] =
    useState<null | ILegalTransactionRelationshipRealEstate>(null);
  const [editReason, setEditReason] = useState<null | IReasonLandRegisterQuery>(
    null
  );

  const handleSetEditRealEstate = (idRealEstate: number) => {
    const realEstate = relationship.RealEstateArray.find(
      rel => rel.idRealEstate === idRealEstate
    );
    const reason = relationship.data.ReasonLandRegisterQueryArray.find(
      res => res.idRealEstate === idRealEstate
    );
    if (realEstate !== undefined) {
      setEditRealEstate(realEstate);
    }
    if (reason !== undefined) {
      setEditReason(reason);
    }
  };

  const handleNewRealEstate = () => {
    const tmpObject = initRealEstate();
    setEditRealEstate(tmpObject);
    setEditReason({
      idRealEstate: tmpObject.idRealEstate,
      Reason: '',
    } as IReasonLandRegisterQuery);
  };

  const handleClose = () => {
    setEditRealEstate(null);
    setEditReason(null);
  };

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleSave();
  };

  const handleRemove = (idRealEstate: number) => {
    setRelationsship({
      ...relationship,
      RealEstateArray: [
        ...relationship.RealEstateArray.filter(
          re => re.idRealEstate !== idRealEstate
        ),
      ],
      data: {
        ReasonLandRegisterQueryArray: [
          ...relationship.data.ReasonLandRegisterQueryArray.filter(
            res => res.idRealEstate !== idRealEstate
          ),
        ],
      },
    });
  };

  const handleSave = () => {
    if (editRealEstate && editReason) {
      const testObject = relationship.RealEstateArray.find(
        rel => rel.idRealEstate === editRealEstate.idRealEstate
      );

      if (testObject) {
        setRelationsship({
          ...relationship,
          RealEstateArray: [
            ...relationship.RealEstateArray.map(rel =>
              rel.idRealEstate === editRealEstate.idRealEstate
                ? editRealEstate
                : rel
            ),
          ],
          data: {
            ReasonLandRegisterQueryArray: [
              ...relationship.data.ReasonLandRegisterQueryArray.map(res =>
                res.idRealEstate === editReason.idRealEstate ? editReason : res
              ),
            ],
          },
        });
      } else {
        setRelationsship({
          ...relationship,
          RealEstateArray: [...relationship.RealEstateArray, editRealEstate],
          data: {
            ReasonLandRegisterQueryArray: [
              ...relationship.data.ReasonLandRegisterQueryArray,
              editReason,
            ],
          },
        });
      }

      handleClose();
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'adress',
      headerName: 'Adresse',
      flex: 1,
      valueGetter: (params: GridValueGetterParams) =>
        `${params.row.Street || ''} ${params.row.StreetNr || ''} ${
          params.row.StreetAditional || ''
        }, ${params.row.City || ''} ${params.row.Postcode || ''}`,
    },

    {
      field: 'Actions',
      headerName: 'Aktionen',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <IconButton
            onClick={() => handleSetEditRealEstate(params.row.idRealEstate)}
          >
            <Edit />
          </IconButton>
          <IconButton onClick={() => handleRemove(params.row.idRealEstate)}>
            <Delete />
          </IconButton>
        </>
      ),
    },
  ];

  return (
    <>
      {editRealEstate && editReason && (
        <Dialog open maxWidth='lg' fullWidth onClose={handleClose}>
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <EditLandRegisterQueryOverview
                configObject={config}
                realEstate={editRealEstate}
                setRealEstate={setEditRealEstate}
                editReason={editReason}
                setEditReason={setEditReason}
              />
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={handleClose}>
                Abbruch
              </Button>
              <Button variant='contained' type='submit'>
                Übernehmen
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      )}

      <Box sx={{ mt: 5 }} />

      <Typography variant='h4' sx={{ mb: 3 }}>
        Grundstücke
        <Button
          sx={{ float: 'right' }}
          variant='contained'
          onClick={handleNewRealEstate}
        >
          Grundstück hinzufügen
        </Button>
      </Typography>

      <br />
      <Box sx={{ height: 400, width: '100%' }}>
        <DataGrid
          sx={{ mt: 2 }}
          autoHeight
          localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
          rows={relationship.RealEstateArray}
          columns={columns}
          getRowId={row => row.idRealEstate}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 5,
              },
            },
          }}
          pageSizeOptions={[5]}
          disableRowSelectionOnClick
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Button variant='outlined' onClick={() => handleBack()} sx={{ mr: 1 }}>
          Zurück
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        <Button
          type='submit'
          variant='contained'
          onClick={() => handleNext()}
          disabled={relationship.RealEstateArray.length === 0}
        >
          Weiter
        </Button>
      </Box>
    </>
  );
};
